<template>
    <v-container fluid class="page-wrap pa-0">

        <FullPageLoader :visible="isLoading" />

        <!-- HERO -->
        <section fluid class="hero-section py-8 text-center mb-4">
            <v-container class="hero-inner" max-width="1200">
                <h1 class="hero-title">
                    Book Your Adventure
                </h1>
            </v-container>
        </section>

        <!-- MAIN CONTENT -->
        <v-container class="content-inner py-10 px-6 px-md-10" max-width="1200">
            <div class="two-rail">
                <!-- LEFT: SAILING SUMMARY -->
                <aside class="left-panel">
                    <v-img :src="hero" alt="Cruise image" height="180" cover class="left-hero" />

                    <div class="left-content">
                        <div class="left-title text-h6 font-weight-bold mb-3">
                            {{ title }}
                        </div>

                        <v-row dense class="mb-1">
                            <v-col cols="5" class="label">Leaving from</v-col>
                            <v-col cols="7" class="value">{{ departFrom }}</v-col>
                        </v-row>

                        <v-row dense class="mb-1">
                            <v-col cols="5" class="label">Onboard</v-col>
                            <v-col cols="7" class="value">{{ shipName }}</v-col>
                        </v-row>

                        <v-row dense class="mb-1">
                            <v-col cols="5" class="label">Dates</v-col>
                            <v-col cols="7" class="value d-flex align-center">
                                <span>{{ startDateFmt }}</span>
                                <v-icon size="18" class="mx-2">mdi-arrow-right</v-icon>
                                <span>{{ endDateFmt }}</span>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4" />

                        <div class="text-subtitle-1 font-weight-bold mb-2">
                            Room selection
                        </div>
                        <v-row dense>
                            <v-col cols="5" class="label">Guests</v-col>
                            <v-col cols="7" class="value">{{ guestsLabel }}</v-col>
                        </v-row>
                    </div>
                </aside>

                <!-- RIGHT: FLOW -->
                <section class="right-rail" v-if="cruise">
                    <!-- Stepper -->
                    <BookingStepper :active-step="activeStep" class="mb-6" />

                    <header class="mb-5">
                        <h2 class="text-h4 font-weight-bold mb-1">Your trip starts here</h2>
                        <p class="text-body-2 text-grey-darken-1">
                            First, let’s confirm the number of guests in your room.
                        </p>
                    </header>

                    <!-- Price per person FROM -->
                    <v-card elevation="3" rounded="lg" class="mb-6 price-card">
                        <div class="text-subtitle-1 price-header text-center py-3 text-white">
                            Total price from
                        </div>

                        <v-card-text class="pb-2 pt-4">
                            <v-row dense>
                                <v-col v-for="cat in roomTypes" :key="cat" cols="12" sm="6" md="3"
                                    class="text-center mb-4 mb-md-0">
                                    <div class="text-caption text-grey-darken-1 mb-1 text-uppercase">
                                        {{ cat }}
                                    </div>
                                    <div class="text-h5 font-weight-bold">
                                        {{ currency(minPrices[cat]) }}
                                    </div>
                                    <div class="text-caption text-grey-darken-1 mt-2">
                                        Includes taxes &amp; fees
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <!-- ROOM CONFIG -->
                    <v-row class="mb-10">
                        <v-col cols="12">
                            <v-card elevation="3" rounded="lg">
                                <v-card-title class="text-subtitle-1 font-weight-bold">
                                    Room configuration
                                </v-card-title>
                                <v-divider />
                                <v-card-text>
                                    <v-row>
                                        <v-col cols="12" md="6" class="mb-4 mb-md-0">
                                            <div class="text-caption text-grey-darken-1 mb-1">Adults</div>
                                            <div class="stepper">
                                                <v-btn icon variant="outlined" :disabled="adults <= 1"
                                                    aria-label="Decrease adults"
                                                    @click="adults = clamp(adults - 1, 1, 4)">
                                                    <v-icon>mdi-minus</v-icon>
                                                </v-btn>

                                                <div class="stepper-value" aria-live="polite">
                                                    {{ adults }}
                                                </div>

                                                <v-btn icon variant="outlined" :disabled="adults >= 4"
                                                    aria-label="Increase adults"
                                                    @click="adults = clamp(adults + 1, 1, 4)">
                                                    <v-icon>mdi-plus</v-icon>
                                                </v-btn>
                                            </div>
                                            <div class="text-caption mt-1">(Age 13+)</div>
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <div class="text-caption text-grey-darken-1 mb-1">Children</div>
                                            <div class="stepper">
                                                <v-btn icon variant="outlined" :disabled="children <= 0"
                                                    aria-label="Decrease children"
                                                    @click="children = clamp(children - 1, 0, 2)">
                                                    <v-icon>mdi-minus</v-icon>
                                                </v-btn>

                                                <div class="stepper-value" aria-live="polite">
                                                    {{ children }}
                                                </div>

                                                <v-btn icon variant="outlined" :disabled="children >= 2"
                                                    aria-label="Increase children"
                                                    @click="children = clamp(children + 1, 0, 2)">
                                                    <v-icon>mdi-plus</v-icon>
                                                </v-btn>
                                            </div>
                                            <div class="text-caption mt-1">(Age 1–12)</div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <div class="d-flex justify-end">
                        <v-btn color="primary" size="large" rounded="lg" class="px-8" :disabled="!isStepValid"
                            @click="continueToCabins">
                            Continue
                        </v-btn>
                    </div>
                </section>
            </div>
        </v-container>
    </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import FullPageLoader from '@/components/FullPageLoader.vue'
import BookingStepper from '@/components/BookingStepper.vue'

export default {
    name: 'DealDetail',
    components: {
        FullPageLoader,
        BookingStepper,
    },
    data() {
        return {
            activeStep: 0,
            cruiseId: null,
            cruise: null,
            roomTypes: ['Inside', 'Outside', 'Balcony', 'Suite'],
            listings: [],
            adults: 2,
            children: 0,
            hasSenior: false,
            country: null,
            state: null
        }
    },
    created() {
        this.cruiseId = this.$route.params.id
        this.getCruise()
        this.fetchListings()
    },
    computed: {
        isStepValid() {
            return !!this.adults && this.adults >= 1
        },
        isLoading() {
            return this.cruise == null
        },
        minPrices() {
            const out = { Inside: null, Outside: null, Balcony: null, Suite: null }
            for (const l of this.listings) {
                const key = (l.room_type || '').trim()
                if (!out[key] || Number(l.total_cost) < Number(out[key])) {
                    out[key] = l.total_cost
                }
            }
            return out
        },
        hero() { return this.cruise?.full_image_url || this.cruise?.imgSrc || '' },
        title() { return this.cruise?.title || '—' },
        departFrom() { return this.cruise?.departure_port || '—' },
        shipName() { return this.cruise?.ship_name || '—' },
        numberOfNights() {
            const m = (this.cruise?.title || '').match(/(\d+)\s*days?/i)
            return m ? parseInt(m[1], 10) : null
        },
        startDateFmt() {
            const ymd = this.cruise?.date
            if (!ymd) return '—'
            const [y, m, d] = ymd.split('-').map(Number)
            const dt = new Date(y, m - 1, d)
            return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        },
        endDateFmt() {
            if (this.cruise?.end_date) {
                const [y, m, d] = this.cruise.end_date.split('-').map(Number)
                return new Date(y, m - 1, d).toLocaleDateString(
                    'en-US',
                    { month: 'short', day: 'numeric', year: 'numeric' }
                )
            }
            if (this.cruise?.date && this.numberOfNights) {
                const [y, m, d] = this.cruise.date.split('-').map(Number)
                const dt = new Date(y, m - 1, d)
                dt.setDate(dt.getDate() + this.numberOfNights)
                return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            }
            return '—'
        },
        guestsLabel() {
            const a = `${this.adults} Adult${this.adults > 1 ? 's' : ''}`
            const c = this.children ? `, ${this.children} Child${this.children > 1 ? 'ren' : ''}` : ''
            return a + c
        }
    },
    methods: {
        continueToCabins() {
            if (!this.isStepValid) return

            const step1Payload = {
                cruiseId: this.cruiseId,
                guestsMeta: {
                    adults: this.adults,
                    children: this.children,
                    hasSenior: this.hasSenior
                },
                residency: {
                    country: this.country,
                    state: this.state
                }
            }

            const draft = { ...step1Payload }
            sessionStorage.setItem('bookingDraft', JSON.stringify(draft))

            this.$router.push({
                name: 'SelectCabin',
                params: { id: this.cruiseId },
                state: { bookingDraft: draft }
            })
        },
        currency(value) {
            if (value == null || value === '') return 'N/A'
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
            }).format(value)
        },
        async getCruise() {
            try {
                const { data, error } = await supabase
                    .from('cruises')
                    .select('*')
                    .eq('id', this.cruiseId)
                    .single()

                if (error) {
                    console.error('Error fetching cruise:', error)
                    return
                }
                this.cruise = data
            } catch (err) {
                console.error('Unexpected error:', err)
            }
        },
        async fetchListings() {
            const sailingID = this.cruiseId
            if (!sailingID) return

            try {
                const { data, error } = await supabase
                    .from('listings')
                    .select()
                    .eq('sailing_id', sailingID)

                if (error) {
                    console.error('Error fetching listings:', error)
                    return
                }
                this.listings = data || []
            } catch (err) {
                console.error('Unexpected error fetching listings:', err)
            }
        },
        clamp(v, min, max) {
            return Math.min(max, Math.max(min, v))
        }
    }
}
</script>

<style scoped>
/* MAIN CONTENT WRAP */
.content-inner {
    margin-top: -16px;
    /* slight overlap feeling */
}

/* 2-rail layout */
.two-rail {
    display: flex;
    align-items: flex-start;
    gap: 24px;
}

/* LEFT PANEL */
.left-panel {
    --sticky-offset: 96px;
    flex: 0 0 360px;
    max-width: 360px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
    position: sticky;
    top: var(--sticky-offset);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.left-hero {
    flex: 0 0 auto;
}

.left-content {
    padding: 18px 18px 20px;
}

.left-title {
    line-height: 1.3;
}

/* RIGHT RAIL */
.right-rail {
    flex: 1 1 auto;
    min-width: 0;
}

/* labels on left summary */
.label {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
}

.value {
    color: rgba(0, 0, 0, 0.87);
}

/* price card header */
.price-card {
    overflow: hidden;
}

.price-header {
    background: var(--color-primary, #2436c8);
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

/* steppers */
.stepper {
    display: inline-flex;
    align-items: center;
    gap: 15px;
    padding: 8px;
    height: 44px;
}

.stepper .v-btn {
    width: 36px;
    height: 36px;
}

.stepper-value {
    min-width: 2ch;
    text-align: center;
    font-weight: 600;
    font-size: 20px;
}

/* MOBILE / TABLET */
@media (max-width: 959.98px) {
    .two-rail {
        flex-direction: column;
    }

    .left-panel {
        position: static;
        max-width: 100%;
    }

    .content-inner {
        padding-inline: 16px !important;
    }
}
</style>
